import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RoadmapPriority } from "@/lib/api";
import { isLanguage, type Language, LANGUAGE_NAMES, LANGUAGES } from "@/lib/i18n/language";

import { isRoadmapPriority, priorityLabel, ROADMAP_PRIORITIES } from "./utils";

/** A text area in `<Input>`'s look; the dashboard has no other. */
export const TEXTAREA_CLASS =
  "border-input bg-card text-foreground ring-offset-background placeholder:text-muted focus-visible:ring-ring flex min-h-24 w-full resize-y rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50";

interface FieldProps {
  label: string;
  htmlFor: string;
  /** A muted line under the control. */
  hint?: string;
  children: ReactNode;
}

/** A form field: its label above the control, and a hint under it. */
export function Field({ label, htmlFor, hint, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-foreground block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-muted text-xs">{hint}</p>}
    </div>
  );
}

interface PrioritySelectProps {
  id?: string;
  value: RoadmapPriority;
  onChange: (priority: RoadmapPriority) => void;
  /** Takes the focus when it shows, as the first control of a form opened in place. */
  autoFocus?: boolean;
}

/** The priority, a themed choice (never a native select): Hoog, Middel or Laag. */
export function PrioritySelect({ id, value, onChange, autoFocus }: PrioritySelectProps) {
  return (
    <Select value={value} onValueChange={(priority) => onChange(priority as RoadmapPriority)}>
      <SelectTrigger id={id} size="sm" autoFocus={autoFocus} className="w-full sm:w-48">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ROADMAP_PRIORITIES.map((priority) => (
          <SelectItem key={priority} value={priority}>
            {priorityLabel(priority)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The optional priority's value for "no preference": a Radix select item cannot have an empty value. */
const NO_PRIORITY = "none";

interface OptionalPrioritySelectProps {
  id?: string;
  /** Null for no preference. */
  value: RoadmapPriority | null;
  onChange: (priority: RoadmapPriority | null) => void;
}

/** A priority that may stay open: Geen voorkeur (the default), Hoog, Middel or Laag. */
export function OptionalPrioritySelect({ id, value, onChange }: OptionalPrioritySelectProps) {
  const { t } = useTranslation();
  return (
    <Select
      value={value ?? NO_PRIORITY}
      onValueChange={(choice) => onChange(isRoadmapPriority(choice) ? choice : null)}
    >
      <SelectTrigger id={id} size="sm" className="w-full sm:w-48">
        <SelectValue>{value ? priorityLabel(value) : t("roadmap.form.noPriority")}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_PRIORITY}>{t("roadmap.form.noPriority")}</SelectItem>
        {ROADMAP_PRIORITIES.map((priority) => (
          <SelectItem key={priority} value={priority}>
            {priorityLabel(priority)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface LanguageSelectProps {
  id?: string;
  value: Language;
  onChange: (language: Language) => void;
}

/** The language a text is written in, a themed choice: each language by its own name. */
export function LanguageSelect({ id, value, onChange }: LanguageSelectProps) {
  return (
    <Select value={value} onValueChange={(choice) => isLanguage(choice) && onChange(choice)}>
      <SelectTrigger id={id} size="sm" className="w-full sm:w-48">
        <SelectValue lang={value}>{LANGUAGE_NAMES[value]}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {LANGUAGES.map((code) => (
          <SelectItem key={code} value={code} lang={code}>
            {LANGUAGE_NAMES[code]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
